import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import styles from '../../../assets/jss/pages/adminSettingCustomOtherStyle';
import Snackbar from '../../../components/Snackbar/Snackbar';
import GridContainer from '../../../components/Grid/GridContainer';
import WrapperContainer from '../../../components/Container/WrapperContainer';
import Typography from '../../../components/Typography/CustomTypography';
import CleanableInput from '../../../components/Input/CleanableInput';
import Button from '../../../components/Button/CustomButton';
import AlertDialog from '../../../components/Dialog/AlertDialog';
import GridItem from '../../../components/Grid/GridItem';
import Validation from '../../../common/Validation';

const useStyles = makeStyles(styles);

const SettingCustomOther = ({
    getSettingCustomComment,
    clearGetSettingCustomComment,
    updateSettingCustomComment,
    clearUpdateSettingCustomComment,
    customComment,
    updateCustomComment,
    updateStatusComment,
    eventInfo,
}) => {
    const classes = useStyles();
    const { shortName } = useParams();
    const intl = useIntl();
    const [params, setParams] = useState({});
    const [validateResults, setValidateResults] = useState({});
    const [isOpenSnackbar, setIsOpenSnackbar] = useState({ isOpen: false, message: '' });
    const [isOpenConfirmSuccess, setIsOpenConfirmSuccess] = useState({ open: false, content: '', type: '' });

    useEffect(() => {
        getSettingCustomComment({
            eventShortName: shortName,
        });
        return () => {
            clearGetSettingCustomComment();
            clearUpdateSettingCustomComment();
        };
    }, []);

    useEffect(() => {
        if (!_.isEmpty(customComment)) {
            setParams({
                content_visitor: customComment?.content_visitor || '',
                content_exhibitor: customComment?.content_exhibitor || '',
                content_visitor_en: customComment?.content_en?.content_visitor || '',
                content_exhibitor_en: customComment?.content_en?.content_exhibitor || '',
            });
        }
    }, [customComment]);

    useEffect(() => {
        if (updateStatusComment) {
            setIsOpenSnackbar({ isOpen: true, message: '更新しました。' });
            getSettingCustomComment({
                eventShortName: shortName,
            });
            clearUpdateSettingCustomComment();
        }
    }, [updateStatusComment]);

    const handleChange = (title, value) => {
        setParams((prev) => ({ ...prev, [title]: value }));
    };

    const validate = () => {
        const validateRules = {
            content_visitor_en: [
                () => !_.isEmpty(eventInfo.languages) && params.content_visitor_en && Validation.validateLatin(params.content_visitor_en.trim()),
            ],
            content_exhibitor_en: [
                () => !_.isEmpty(eventInfo.languages) && params.content_exhibitor_en && Validation.validateLatin(params.content_exhibitor_en.trim()),
            ],
        };
        const validateResult = Validation.validateAllRules(validateRules);

        if (Object.keys(validateResult).length > 0) {
            setValidateResults({ ...validateResult });
            window.scrollTo(0, 0);
            return false;
        }
        setValidateResults({});
        return true;
    };

    const handleReset = () => {
        setParams({});
        setValidateResults({});
        let dataSb = {
            content: {},
        };
        if (!_.isEmpty(eventInfo.languages)) {
            dataSb = {
                ...dataSb,
                content: {
                    ...dataSb.content,
                    content_en: {},
                },
            };
        }
        updateSettingCustomComment({
            eventShortName: shortName,
            data: dataSb,
        });
    };

    const handleCancel = () => {
        setParams(customComment);
    };

    const handleSubmit = () => {
        if (validate()) {
            setIsOpenConfirmSuccess({ open: true, content: '適用して宜しいですか。', type: 'submit' });
        }
    };

    const handleSuccessDialog = (type) => {
        setIsOpenConfirmSuccess({ open: false, content: '', type: '' });
        let sbData = {
            content: {
                content_visitor: params.content_visitor || '',
                content_exhibitor: params.content_exhibitor || '',
            },
        };
        if (!_.isEmpty(eventInfo.languages)) {
            sbData = {
                ...sbData,
                content: {
                    ...sbData.content,
                    content_en: {
                        content_visitor: params.content_visitor_en || '',
                        content_exhibitor: params.content_exhibitor_en || '',
                    },
                },
            };
        }
        if (type === 'submit') {
            updateSettingCustomComment({
                eventShortName: shortName,
                data: sbData,
            });
        } else if (type === 'reset') {
            handleReset();
        } else if (type === 'cancel') {
            handleCancel();
        }
    };

    return (
        <>
            <GridContainer className={classes.container}>
                <WrapperContainer>
                    <GridContainer className={classes.customSite}>
                        <GridItem>
                            <Typography bold>{_.isEmpty(eventInfo.languages) ? 'Visitor comment (Japanese)' : 'Visitor comment'}</Typography>
                        </GridItem>
                    </GridContainer>
                    <GridContainer>
                        <GridItem>
                            <CleanableInput
                                rows={10}
                                isMultiline
                                defaultValue={params.content_visitor}
                                onChange={(value) => handleChange('content_visitor', value.trim())}
                            />
                        </GridItem>
                    </GridContainer>
                    {!_.isEmpty(eventInfo.languages) && (
                        <GridContainer className={classes.customSiteExhibitor}>
                            <GridItem>
                                <Typography bold>Visitor comment(English)</Typography>
                            </GridItem>
                        </GridContainer>
                    )}
                    {!_.isEmpty(eventInfo.languages) && (
                        <GridContainer>
                            <GridItem>
                                <CleanableInput
                                    rows={10}
                                    isMultiline
                                    defaultValue={params.content_visitor_en}
                                    onChange={(value) => handleChange('content_visitor_en', value.trim())}
                                    error={!!validateResults.content_visitor_en}
                                    helperText={validateResults.content_visitor_en
                                        ? intl.formatMessage({ id: validateResults.content_visitor_en.message }) : ''}
                                />
                            </GridItem>
                        </GridContainer>
                    )}
                    <GridContainer className={classes.customSiteExhibitor}>
                        <GridItem>
                            <Typography bold>{_.isEmpty(eventInfo.languages) ? 'Exhibitor comment (Japanese)' : 'Exhibitor comment'}</Typography>
                        </GridItem>
                    </GridContainer>
                    <GridContainer>
                        <GridItem>
                            <CleanableInput
                                rows={10}
                                isMultiline
                                defaultValue={params.content_exhibitor}
                                onChange={(value) => handleChange('content_exhibitor', value.trim())}
                            />
                        </GridItem>
                    </GridContainer>
                    {!_.isEmpty(eventInfo.languages) && (
                        <GridContainer className={classes.customSiteExhibitor}>
                            <GridItem>
                                <Typography bold>Exhibitor comment(English)</Typography>
                            </GridItem>
                        </GridContainer>
                    )}
                    {!_.isEmpty(eventInfo.languages) && (
                        <GridContainer>
                            <GridItem>
                                <CleanableInput
                                    rows={10}
                                    isMultiline
                                    defaultValue={params.content_exhibitor_en}
                                    onChange={(value) => handleChange('content_exhibitor_en', value.trim())}
                                    error={!!validateResults.content_exhibitor_en}
                                    helperText={validateResults.content_exhibitor_en
                                        ? intl.formatMessage({ id: validateResults.content_exhibitor_en.message }) : ''}
                                />
                            </GridItem>
                        </GridContainer>
                    )}
                    <GridContainer className={classes.customFooter}>
                        <GridItem className={classes.customFooterButton}>
                            <Button
                                color="info"
                                round
                                gradient
                                size="md"
                                onClick={() => handleSubmit()}
                                className={classes.customButton}
                            >
                                <b>Apply</b>
                            </Button>
                            <Button
                                color="info"
                                round
                                gradient
                                size="md"
                                onClick={() => setIsOpenConfirmSuccess({ open: true, content: 'リセットして宜しいですか。', type: 'reset' })}
                                className={classes.customButtonReset}
                            >
                                <b>Reset</b>
                            </Button>
                            <Button
                                color="info"
                                round
                                gradient
                                size="md"
                                onClick={() => setIsOpenConfirmSuccess({ open: true, content: '入力した情報をキャンセルして宜しいですか。', type: 'cancel' })}
                                className={classes.customButton}
                            >
                                <b>Cancel</b>
                            </Button>
                        </GridItem>
                    </GridContainer>
                </WrapperContainer>
            </GridContainer>
            <Snackbar
                open={isOpenSnackbar.isOpen}
                onClose={() => setIsOpenSnackbar({ isOpen: false, message: '' })}
                message={isOpenSnackbar.message}
                severity="success"
                autoHideDuration={3000}
            />
            <AlertDialog
                title="確認ダイアログ"
                handleClose={() => setIsOpenConfirmSuccess({ open: false, content: '', type: '' })}
                handleOpen={isOpenConfirmSuccess.open}
                handleSubmit={() => handleSuccessDialog(isOpenConfirmSuccess.type)}
                contents={isOpenConfirmSuccess.content}
            />
        </>
    );
};

SettingCustomOther.defaultProps = {
    getSettingCustomComment: () => {},
    clearGetSettingCustomComment: () => {},
    updateSettingCustomComment: () => {},
    clearUpdateSettingCustomComment: () => {},
    customComment: {},
    updateCustomComment: {},
    updateStatusComment: false,
    eventInfo: {},
};

SettingCustomOther.propTypes = {
    getSettingCustomComment: PropTypes.func,
    clearGetSettingCustomComment: PropTypes.func,
    updateSettingCustomComment: PropTypes.func,
    clearUpdateSettingCustomComment: PropTypes.func,
    customComment: PropTypes.oneOfType([() => null, PropTypes.object]),
    updateCustomComment: PropTypes.oneOfType([() => null, PropTypes.object]),
    updateStatusComment: PropTypes.bool,
    eventInfo: PropTypes.oneOfType([() => null, PropTypes.object]),
};

export default SettingCustomOther;
